import * as React from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import './styles.css';
import type { InternalUser, ExternalUser } from 'types/user.types';
import type { Customer } from 'types/customer.types';
import type { Asset } from 'types/asset.types';
import type { Notification, RuleNotifications } from 'types/notification.types';
import { type IRowNode, type ColDef } from 'ag-grid-community';
import type { Operator } from 'types/operator.types';
import { type WorkOrdersSquare } from 'types/squares.type';

import { type Device } from 'types/device.types';
import { type Tablet } from 'types/tablet.types';
import { useAppDispatch, useAppSelector } from 'store/hook';
import ToExcel from 'components/Exports/ToExcel';
import { extractTableData } from 'utils/exports/excel';
import { selectExportFormat } from 'store/exportData.slice';
import ToPdf from 'components/Exports/ToPdf';
import { isDarkTheme } from 'utils/theme';
import { type RoughRoadResponse } from 'types/roadConditions.types';
import { type MakeModelDetails } from 'types/makeModel.types';
import { type TireInventory } from 'types/tire-inventory.types';
import { clientThemePalette, CLIENT_THEME } from 'utils/clientTheme';
import { alpha } from '@mui/material/styles';
import {
  type TireInstallation,
  type PerformanceLogDetail,
} from 'types/tire-installation.types';

type SupportedTypes =
  | InternalUser
  | Customer
  | ExternalUser
  | Asset
  | Operator
  | Notification
  | WorkOrdersSquare
  | Device
  | Tablet
  | RuleNotifications
  | RoughRoadResponse
  | MakeModelDetails
  | TireInventory
  | TireInstallation
  | PerformanceLogDetail;

interface Props<T extends SupportedTypes> {
  rowData: T[];
  columnDefs: ColDef[];
  /**
   * Enable or disable the table `filter`
   */
  isExternalFilterPresent?: boolean;
  /**
   * Filter for AgGrid.
   * Returns true to keep an item. Returns false to remove an item
   */
  filter?: (node: IRowNode<T>) => boolean;
  cacheQuickFilter?: boolean;
  searchValue?: string;
  rowSelection?: boolean;
  exportTo?: boolean;
  handleExport?: (val: boolean) => void;
  pagination?: boolean;
  paginationPageSize?: number;
  paginationAutoPageSize?: boolean;
  exportToExcel?: boolean;
  handleExportToExcel?: (val: boolean) => void;
  onGridReady?: (params: any) => void;
  tableStyles?: any;
  getRowStyle?: any;
  exportPdf?: boolean;
  /**
   * Set cursor to pointer for all rows
   */
  rowCursorPointer?: boolean;
  getRowId?: any;
  onCellValueChanged?: (params: any) => void;
  /**
   * Enable single-click editing instead of double-click
   */
  singleClickEdit?: boolean;
  /**
   * Set the height of each row
   */
  rowHeight?: number;
}

/**
 * This component acts as the base component for data list tables
 * Cell Renderer is the react component which is displayed in the cell which its assigned to
 */
export function BaseTableList<T extends SupportedTypes>({
  rowData,
  columnDefs,
  pagination = false, // Default value for pagination
  paginationPageSize = 10, // Default value for paginationPageSize
  paginationAutoPageSize = false, // Default value for paginationAutoPageSize
  exportPdf = true,
  rowCursorPointer = false, // Default value for rowCursorPointer
  getRowId = undefined,
  onCellValueChanged,
  ...props
}: Props<T>) {
  const defaultColDef: ColDef<T> = {
    flex: 1,
    resizable: true,
    minWidth: 100,
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
    },
  };
  const gridRef = React.useRef<AgGridReact<T>>(null);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const localeText = React.useMemo(
    () => ({
      page: t('ag_grid_page'),
      to: t('ag_grid_to'),
      of: t('ag_grid_of'),
      next: t('ag_grid_next'),
      last: t('ag_grid_last'),
      first: t('ag_grid_first'),
      previous: t('ag_grid_previous'),
      noRowsToShow: t('ag_grid_no_rows'),
      loadingOoo: t('ag_grid_loading'),
    }),
    [t]
  );

  const exportFormat = useAppSelector(selectExportFormat);
  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const enableDarkTheme = isDarkTheme(theme);

  // Apply custom row styling when rowCursorPointer is true
  const getRowStyleWithPointer = React.useCallback(
    (params: any) => {
      // Combine any existing row styles with the pointer cursor style
      const existingStyles = props.getRowStyle ? props.getRowStyle(params) : {};

      return rowCursorPointer
        ? { ...existingStyles, cursor: 'pointer' }
        : existingStyles;
    },
    [rowCursorPointer, props.getRowStyle]
  );

  const setQuickFilterCallback = React.useCallback(() => {
    if (!gridRef.current?.api) return;
    gridRef.current.api.setQuickFilter(props.searchValue ?? '');
  }, [props.searchValue]);

  // This is required to inform AgGrid that the rows need to be re-filtered
  // https://www.ag-grid.com/react-data-grid/filter-external/#doesexternalfilterpass
  React.useEffect(() => {
    if (!gridRef.current?.api) return;
    gridRef.current.api.onFilterChanged();

    // this enables a quick search
    setQuickFilterCallback();
  }, [gridRef.current, props.isExternalFilterPresent, props.searchValue]);

  const [toPrint, setToPrint] = React.useState(false);

  //  set toPrint back to false without page re-render
  const handleToPrint = (val: boolean) => {
    setToPrint(val);
    if (val) {
      setTimeout(() => {
        setToPrint(false);
      }, 4000);
    }
  };

  React.useEffect(() => {
    if (props.exportTo && props.handleExport) {
      extractTableData({ handleToPrint, gridRef, dispatch });
      props.handleExport(false);
    }
  }, [props.exportTo, props.handleExport]);

  const _cp = clientThemePalette[CLIENT_THEME];

  const gridStyle: React.CSSProperties & Record<string, string | number> = {
    width: '100%',
    height: '894px',
    overflow: 'auto',
    '--ag-header-background-color': enableDarkTheme
      ? _cp.dark.tableHeader
      : _cp.light.primary,
    '--ag-header-foreground-color': '#ffffff',
    '--ag-background-color': enableDarkTheme
      ? _cp.dark.pageBackground
      : '#ffffff',
    '--ag-odd-row-background-color': enableDarkTheme
      ? _cp.dark.pageBackground
      : '#ffffff',
    '--ag-even-row-background-color': enableDarkTheme
      ? _cp.dark.boxBackground
      : '#f3f4f6',
    '--ag-border-color': enableDarkTheme ? _cp.dark.borderGrey : '#BDC3C7',
    '--ag-row-hover-color': enableDarkTheme
      ? alpha(_cp.dark.primary, 0.08)
      : alpha(_cp.light.primary, 0.04),
    '--ag-foreground-color': enableDarkTheme ? '#ffffff' : '#000000',
    '--ag-secondary-foreground-color': enableDarkTheme
      ? 'rgba(255,255,255,0.7)'
      : 'rgba(0,0,0,0.7)',
    '--ag-row-border-color': enableDarkTheme
      ? _cp.dark.borderGrey
      : 'rgba(0,0,0,0.08)',
    ...props.tableStyles,
  };

  return (
    <div
      className={enableDarkTheme ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'}
      style={gridStyle}
    >
      <AgGridReact
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowSelection={props.rowSelection ? 'multiple' : undefined}
        // isExternalFilterPresent is called exactly once every time the grid senses a filter change
        // e.g: api.onFilterChanged
        // https://www.ag-grid.com/react-data-grid/filter-external/
        isExternalFilterPresent={() => !!props.isExternalFilterPresent}
        // This runs when isExternalFilterPresent returns `true`.
        // The callback is called for every row of AgGrid (`rowData`)
        // If the filter returns true then the row is kept. Otherwise the row is removed
        doesExternalFilterPass={props.filter ?? (() => true)}
        cacheQuickFilter={props.cacheQuickFilter ?? false}
        localeText={localeText}
        tooltipShowDelay={0}
        tooltipHideDelay={2000}
        // Using the props with default values
        pagination={pagination}
        paginationPageSize={paginationPageSize}
        paginationAutoPageSize={paginationAutoPageSize}
        onGridReady={props.onGridReady}
        getRowStyle={getRowStyleWithPointer}
        enableBrowserTooltips={true}
        getRowId={getRowId}
        onCellValueChanged={onCellValueChanged}
        singleClickEdit={props.singleClickEdit ?? true}
        rowHeight={props.rowHeight}
      ></AgGridReact>
      {toPrint && props.handleExport && exportFormat === 'excel' && <ToExcel />}
      {toPrint && props.handleExport && exportFormat === 'pdf' && exportPdf && (
        <ToPdf />
      )}
    </div>
  );
}
